/**
 * Rendering a system line, with no imports — see `system-message.ts`.
 *
 * Both thread kinds use it. A group stores its key and parameters on the row;
 * a one-to-one chat derives the key per viewer in `presentMessageForViewer`
 * and carries no parameters, so `systemParams` is absent there rather than
 * empty. One renderer either way: the key is what says a line was written by
 * the app rather than by a person.
 *
 * The `-core` split is the house pattern (`outbox-core`,
 * `conversation-deck-cache-core`) and it earns itself here: two of the three
 * fallback rungs below CANNOT be reached in development, and a test that has
 * to boot the React Native runtime to reach them would not be written.
 */
export type SystemMessage = {
  content: string;
  systemKey?: string | null;
  systemParams?: Record<string, string> | null;
};

/** What `i18n.t` does, as a parameter — see `renderSystemMessage` below. */
export type Translate = (key: string, params?: Record<string, string>) => string;

/**
 * The logic, with the translator passed in.
 *
 * Separated only so it can be tested. Importing `@/i18n` in a node test pulls
 * the React Native runtime with it, and the branch worth pinning is the one
 * that CANNOT be reached in development: a client one release behind the API,
 * holding a key its bundle has never seen.
 */
export function renderSystemMessage(
  message: SystemMessage,
  t: Translate,
): string {
  const key = message.systemKey;
  if (!key) return message.content;

  // A missing display name is the server's "Someone", translated here rather
  // than baked into the parameter — the parameter is data, and "Someone" is
  // copy. Set unconditionally rather than per-key: a template with no
  // `{{name}}` — every chat line is one — simply never reads it.
  const params: Record<string, string> = { ...message.systemParams };
  if (!params.name) params.name = t("social.groups.system.someone");

  const rendered = t(key, params);
  // i18next answers with the KEY when it has no entry for it. That is the
  // one-release-behind case, and the English the server sent reads far better
  // than "social.groups.system.joined".
  return rendered && rendered !== key ? rendered : message.content;
}
